import { Button } from '@ai-tutor/ui';

/**
 * The history sidebar's two non-list bodies — ROUTE_INVENTORY §2.11 #22.
 *
 * They exist as a PAIR, which is the behaviour: `usePracticeHistoryState`
 * exposes `isLoading` separately from the list precisely so a first fetch
 * shimmers instead of flashing "No sessions yet" and then popping rows in.
 * A component that rendered the empty state while loading would look correct
 * in every screenshot and wrong every time the network was slow.
 */

/** Shown only once the first fetch has resolved on an empty history. */
export function HistoryEmptyState() {
  return (
    <div className='flex h-full flex-col items-center justify-center px-6 py-12 text-center'>
      <p className='text-xs font-medium text-muted-foreground'>
        No sessions yet
      </p>
      <p className='mt-1 text-xs leading-relaxed text-muted-foreground'>
        Your practice history will appear here.
      </p>
    </div>
  );
}

/**
 * The first fetch FAILED. Same footprint as the empty state, because it sits
 * in the same slot — but it must never read as "you have no sessions", which
 * is what the sidebar showed before it could tell the two apart.
 */
export function HistoryErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className='flex h-full flex-col items-center justify-center px-6 py-12 text-center'>
      <p className='text-xs font-medium text-foreground'>
        Couldn&apos;t load your history
      </p>
      <p className='mt-1 text-xs leading-relaxed text-muted-foreground'>
        Your sessions are safe. Check your connection and try again.
      </p>
      <Button variant='outline' size='sm' className='mt-3' onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

/**
 * Loading placeholder shaped like the date-grouped row list — a heading block
 * plus rows, twice — so the fetch resolves into the same geometry rather than
 * shifting it. `aria-hidden`, because a shimmer has nothing to announce.
 */
export function HistorySkeleton() {
  const groups = [3, 2];
  return (
    <div className='space-y-3 px-2 pb-3 pt-2' aria-hidden='true'>
      {groups.map((rows, gi) => (
        <section key={gi}>
          <div className='mb-1 ml-2 h-2.5 w-16 animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
          <ul className='space-y-0.5'>
            {Array.from({ length: rows }).map((_, ri) => (
              <li key={ri} className='flex items-start gap-2 px-2 py-2'>
                <div className='mt-0.5 h-3.5 w-3.5 flex-shrink-0 animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
                <div className='min-w-0 flex-1 space-y-1.5'>
                  <div className='flex items-center justify-between gap-2'>
                    <div className='h-2.5 w-24 animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
                    <div className='h-2.5 w-8 animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
                  </div>
                  <div className='h-2.5 w-2/3 animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
